import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/api";
import { errorMessage } from "@/api/client";
import type { HistoryItem } from "@/api/types";
import {
  Badge,
  Button,
  ErrorBanner,
  HtmlFrame,
  PageHeader,
  Spinner,
} from "@/components/ui";
import { formatDateTime } from "@/lib/dates";

const STATUS_TONE = {
  sent: "good",
  failed: "critical",
  "dry-run": "warning",
} as const;

function HistoryDetail({
  item,
  onClose,
}: {
  item: HistoryItem;
  onClose: () => void;
}) {
  const html = useQuery({
    queryKey: ["history-html", item.id],
    queryFn: () => api.historyHtml(item.id),
  });
  return (
    <div className="mb-6">
      <div className="mb-2 flex items-center justify-between">
        <strong>{item.subject}</strong>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
      {item.error && <ErrorBanner message={item.error} />}
      {html.isPending && <Spinner />}
      <ErrorBanner message={html.error ? errorMessage(html.error) : null} />
      {html.data !== undefined && (
        <HtmlFrame html={html.data} title={item.subject} />
      )}
    </div>
  );
}

export default function HistoryPage() {
  const [selected, setSelected] = useState<HistoryItem | null>(null);
  const history = useQuery({ queryKey: ["history"], queryFn: api.listHistory });

  return (
    <>
      <PageHeader title="History" />
      <ErrorBanner
        message={history.error ? errorMessage(history.error) : null}
      />
      {selected && (
        <HistoryDetail item={selected} onClose={() => setSelected(null)} />
      )}
      {history.isPending ? (
        <Spinner />
      ) : (
        <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-neutral-100 text-neutral-600">
              <tr>
                <th className="p-3">Sent</th>
                <th className="p-3">Type</th>
                <th className="p-3">Subject</th>
                <th className="p-3">Range</th>
                <th className="p-3">Recipients</th>
                <th className="p-3">Trigger</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {history.data?.map((h) => (
                <tr
                  key={h.id}
                  onClick={() => setSelected(h)}
                  className={`cursor-pointer border-t border-neutral-200 hover:bg-neutral-50 ${selected?.id === h.id ? "bg-neutral-50" : ""}`}
                >
                  <td className="p-3">{formatDateTime(h.sentAt)}</td>
                  <td className="p-3">
                    <Badge>{h.reportType}</Badge>
                  </td>
                  <td className="p-3">{h.subject}</td>
                  <td className="p-3">
                    {h.rangeStart} – {h.rangeEnd}
                  </td>
                  <td className="p-3">{h.recipientCount}</td>
                  <td className="p-3">{h.trigger}</td>
                  <td className="p-3">
                    <Badge tone={STATUS_TONE[h.status]}>{h.status}</Badge>
                  </td>
                </tr>
              ))}
              {history.data?.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-neutral-500">
                    No reports sent yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
